import React, { useState, useEffect } from 'react';
import { Gift, Plus, Users, TrendingUp, Trash2, Clock, Award, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const AdminCreditsPanel = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(5);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState({ totalUsers: 0, totalCredits: 0, totalRedeemed: 0, totalHours: 0 });
  const [sortBy, setSortBy] = useState('hours'); // 'hours', 'name', 'date'

  const backendUrl = '';

  useEffect(() => {
    fetchAllCredits();
  }, []);

  const fetchAllCredits = async () => {
    try {
      setLoading(true);
      
      // Fetch users with hours data from new endpoint
      const usersWithHoursResponse = await fetch(`${backendUrl}/api/auth/admin/users-with-hours`);
      let usersWithHours = [];
      
      if (usersWithHoursResponse.ok) {
        usersWithHours = await usersWithHoursResponse.json();
      }
      
      // Fallback to old endpoint if new one fails
      if (usersWithHours.length === 0) {
        const usersResponse = await fetch(`${backendUrl}/api/auth/users`);
        const usersData = await usersResponse.json();
        usersWithHours = usersData.map(u => ({
          ...u,
          total_hours_booked: 0,
          total_bookings: 0,
          free_credits_earned: 0,
          credits_available: 0
        }));
      }
      
      // Also fetch credits data
      const creditsResponse = await fetch(`${backendUrl}/api/credits/admin/all-users`);
      const creditsData = await creditsResponse.json();
      
      // Merge credits data with users
      const mergedUsers = usersWithHours.map(user => {
        const userCredits = creditsData.users?.find(u => u.email === user.email);
        return {
          ...user,
          available: userCredits?.available || user.credits_available || 0,
          total_redeemed: userCredits?.total_redeemed || 0
        };
      });
      
      // Calculate total hours
      const totalHours = mergedUsers.reduce((sum, u) => sum + (u.total_hours_booked || 0), 0);
      
      setAllUsers(mergedUsers);
      setStats({
        totalUsers: mergedUsers.length || 0,
        totalCredits: creditsData.total_available_credits || 0,
        totalRedeemed: creditsData.total_redeemed_credits || 0,
        totalHours: totalHours
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load user data' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser) {
      setMessage({ type: 'error', text: 'Please select a user' });
      return;
    }

    if (creditsToAdd <= 0) {
      setMessage({ type: 'error', text: 'Credits must be positive' });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/credits/admin/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: selectedUser,
          credits: parseInt(creditsToAdd),
          reason: reason || 'Admin bonus credits'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: `✅ Added ${creditsToAdd} credits to ${selectedUser}` });
        setCreditsToAdd(5);
        setReason('');
        setSelectedUser(null);
        fetchAllCredits(); // Refresh the list
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to add credits' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };


  const handleDeleteUser = async (userEmail, userName) => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete the user account and ALL associated data including:\n\n• User account\n• All bookings\n• Credits\n• Referral code\n• Notifications\n\nUser: ${userName} (${userEmail})\n\nThis action CANNOT be undone. Are you absolutely sure?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/users/${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`User ${userEmail} deleted successfully`);
        fetchAllCredits(); // Refresh the list
      } else {
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Network error. Please try again.');
    }
  };

  const handleResetPassword = async (userEmail, userName) => {
    const newPassword = window.prompt(
      `Set new password for ${userName || userEmail}.\n\n` +
      `Tip: Use something simple they can remember (e.g. their name + year).\n` +
      `They can change it later from their account settings.`,
      ''
    );
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const response = await fetch(`${backendUrl}/api/auth/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Password set for ${userEmail}. Tell them: "${newPassword}"`);
        fetchAllCredits();
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Network error. Please try again.');
    }
  };

  // Sort users
  const sortedUsers = [...allUsers].sort((a, b) => {
    if (sortBy === 'hours') return (b.total_hours_booked || 0) - (a.total_hours_booked || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'date') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    return 0;
  });


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Gift className="text-purple-400" size={40} />
            Users & Credits
          </h1>
          <p className="text-gray-400">Manage users, view hours booked, and distribute credits</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 backdrop-blur-sm border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-xs mb-1">Total Users</p>
                <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
              </div>
              <Users className="text-purple-400" size={28} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 backdrop-blur-sm border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-300 text-xs mb-1">Total Hours</p>
                <p className="text-2xl font-bold text-white">{stats.totalHours}</p>
              </div>
              <Clock className="text-orange-400" size={28} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 backdrop-blur-sm border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-xs mb-1">Available Credits</p>
                <p className="text-2xl font-bold text-white">{stats.totalCredits}</p>
              </div>
              <Gift className="text-blue-400" size={28} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 backdrop-blur-sm border border-green-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-300 text-xs mb-1">Redeemed</p>
                <p className="text-2xl font-bold text-white">{stats.totalRedeemed}</p>
              </div>
              <TrendingUp className="text-green-400" size={28} />
            </div>
          </div>
        </div>

        {/* Add Credits Form */}
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Plus className="text-purple-400" />
            Add Credits to User
          </h2>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/50 text-green-300'
                : 'bg-red-500/20 border border-red-500/50 text-red-300'
            }`}>
              {message.text}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 mb-2 font-semibold">Select User</label>
              <select
                value={selectedUser || ''}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">-- Choose a user --</option>
                {allUsers.map((user) => (
                  <option key={user.email} value={user.email}>
                    {user.name || user.email.split('@')[0]} - {user.total_hours_booked || 0}hrs - {user.available || 0} credits
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-semibold">Credits to Add</label>
              <input
                type="number"
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(e.target.value)}
                min="1"
                className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                placeholder="e.g., 10"
              />
              <p className="text-gray-500 text-sm mt-1">3 credits = 1 hour booking</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-300 mb-2 font-semibold">Reason (optional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                placeholder="e.g., Loyalty reward, Compensation, etc."
              />
            </div>
          </div>

          <button
            onClick={handleAddCredits}
            className="mt-6 w-full md:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <Plus size={20} />
            Add Credits
          </button>
        </div>

        {/* Users Database */}
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="text-purple-400" />
              User Database ({allUsers.length})
            </h2>
            
            {/* Sort Options */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none"
              >
                <option value="hours">Hours (Most)</option>
                <option value="name">Name (A-Z)</option>
                <option value="date">Newest First</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading...</p>
            </div>
          ) : allUsers.length === 0 ? (
            <p className="text-gray-400 text-center py-12">No users registered yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">Name</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">Email</th>
                    <th className="text-center py-3 px-4 text-gray-300 font-semibold">
                      <div className="flex items-center justify-center gap-1">
                        <Clock size={14} />
                        Hours
                      </div>
                    </th>
                    <th className="text-center py-3 px-4 text-gray-300 font-semibold">Bookings</th>
                    <th className="text-center py-3 px-4 text-gray-300 font-semibold">
                      <div className="flex items-center justify-center gap-1">
                        <Award size={14} />
                        Credits
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">Joined</th>
                    <th className="text-center py-3 px-4 text-gray-300 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user, index) => (
                    <tr key={user.email || index} className={`border-b border-gray-800 hover:bg-gray-800/40 transition-colors ${index % 2 === 0 ? 'bg-gray-800/20' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${user.can_sign_in ? 'bg-green-500' : 'bg-red-500'}`}
                            title={user.can_sign_in ? 'Can actively sign in' : 'Cannot sign in (no password set)'}
                            data-testid={`signin-dot-${user.email}`}
                          />
                          <span className="text-white font-medium">{user.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300 text-xs">{user.email}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full font-bold ${
                          (user.total_hours_booked || 0) >= 10 
                            ? 'bg-orange-500/30 text-orange-300' 
                            : (user.total_hours_booked || 0) >= 5
                            ? 'bg-yellow-500/30 text-yellow-300'
                            : (user.total_hours_booked || 0) > 0
                            ? 'bg-blue-500/30 text-blue-300'
                            : 'bg-gray-700/50 text-gray-500'
                        }`}>
                          {user.total_hours_booked || 0}h
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-400">{user.total_bookings || 0}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full font-semibold ${
                          (user.available || 0) > 0 
                            ? 'bg-purple-500/20 text-purple-300' 
                            : 'bg-gray-700/50 text-gray-500'
                        }`}>
                          {user.available || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-xs">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        }) : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleResetPassword(user.email, user.name)}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1"
                            title="Set / reset password for this user"
                            data-testid={`reset-pw-btn-${user.email}`}
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.email, user.name)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1"
                            title="Delete user account"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCreditsPanel;